import { Pencil, StickyNote } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { createContext, useEffect, useRef, useState } from 'react';

import { getProviderInitials } from '../../helpers';
import { SmallCaps, TopicBand } from '../Visit';
import styles from './HuddleNotes.module.scss';

export const HuddleNotesContext = createContext({
  notesByApptId: {},
  huddleNoteCountByPatientId: {},
  huddleNoteVisitsByPatientId: {},
  huddleVisitLabelById: {},
  huddleNextVisitByPatientId: {},
  onPostNote: null,
  onEditNote: null,
});

export const NOTE_SOURCE_LABEL = {
  prepare: 'from Prepare',
  'post-visit': 'from post-visit follow-up',
  task: 'from a task',
  'provider-hud': 'from Provider HUD',
};

export const formatNoteStamp = (at) => {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return '';
  const time = date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  if (date.toDateString() === new Date().toDateString()) return time;
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
};

const HuddleNotes = ({
  notes = [],
  onPostNote,
  onEditNote,
  subtitle,
  autoFocusComposer = false,
  composerVisitLabel,
  variant = 'section',
}) => {
  const isPanel = variant === 'panel';
  const threadClassName = isPanel
    ? `${styles.threadRow} ${styles.panelThreadRow} ${styles.compact}`
    : styles.threadRow;
  const composerRef = useRef(null);
  const editRef = useRef(null);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditDraft(note.text);
  };

  const cancelEdit = () => setEditingId(null);

  useEffect(() => {
    if (autoFocusComposer) composerRef.current?.focus();
  }, [autoFocusComposer]);

  useEffect(() => {
    if (editingId) editRef.current?.focus();
  }, [editingId]);

  const saveEdit = async (note) => {
    const text = editDraft.trim();
    if (!text || isSavingEdit) return;
    if (text === note.text) {
      cancelEdit();
      return;
    }
    setIsSavingEdit(true);
    const ok = await onEditNote(note, text);
    setIsSavingEdit(false);
    if (ok) cancelEdit();
  };

  const postDraft = () => {
    const text = draft.trim();
    if (!text) return;
    onPostNote(text);
    setDraft('');
  };

  return (
    <>
      {!isPanel && (
        <TopicBand
          density="card"
          label="Huddle notes"
          meta={
            notes.length > 0 ? `${notes.length} ${notes.length === 1 ? 'note' : 'notes'}` : null
          }
        />
      )}
      {subtitle ? (
        <p className={`${styles.subtitle} ${isPanel ? styles.panelSubtitle : ''}`}>{subtitle}</p>
      ) : null}

      {isPanel && notes.length === 0 ? (
        <div className={`${styles.threadRow} ${styles.panelThreadRow}`}>
          <p className={styles.emptyText}>No notes on this visit yet.</p>
        </div>
      ) : null}

      {notes.length > 0 && (
        <div className={threadClassName}>
          {notes.map((note, index) => (
            <React.Fragment key={note.id}>
              {note.visitLabel && note.visitLabel !== notes[index - 1]?.visitLabel ? (
                <div className={styles.visitLabel}>{note.visitLabel}</div>
              ) : null}
              <div className={styles.note}>
                <span className={styles.avatar} aria-hidden="true">
                  {getProviderInitials(note.author)}
                </span>
                <div className={styles.noteBody}>
                  {editingId === note.id ? (
                    <>
                      <textarea
                        className={styles.composer}
                        aria-label="Edit huddle note"
                        ref={editRef}
                        value={editDraft}
                        onChange={(event) => setEditDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            cancelEdit();
                          }
                          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                            event.preventDefault();
                            saveEdit(note);
                          }
                        }}
                      />
                      <div className={styles.editButtons}>
                        <button
                          type="button"
                          className={`${styles.postButton} ${styles.editActionButton}`}
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className={`${styles.postButton} ${styles.editActionButton} ${styles.saveButton}`}
                          disabled={!editDraft.trim() || isSavingEdit}
                          onClick={() => saveEdit(note)}
                        >
                          Save
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className={styles.noteText}>{note.text}</div>
                  )}
                  <div className={styles.noteMetaRow}>
                    <div className={styles.noteMeta}>
                      {note.author} · {formatNoteStamp(note.createdAt)}
                      {note.editedBy && note.editedAt
                        ? ` · Edited by ${note.editedBy} · ${formatNoteStamp(note.editedAt)}`
                        : ''}
                      {NOTE_SOURCE_LABEL[note.source] ? (
                        <span className={styles.sourceTag}>{NOTE_SOURCE_LABEL[note.source]}</span>
                      ) : null}
                    </div>
                    {onEditNote && editingId !== note.id ? (
                      <button
                        type="button"
                        className={styles.editButton}
                        aria-label={`Edit note from ${note.author}`}
                        onClick={() => startEdit(note)}
                      >
                        <Pencil size={11} aria-hidden="true" />
                        Edit
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      <div className={styles.composerRow}>
        {isPanel ? (
          <SmallCaps as="div" className={styles.composerLabel}>
            Add a note
            {composerVisitLabel ? (
              <span className={styles.composerVisit}> · {composerVisitLabel}</span>
            ) : null}
          </SmallCaps>
        ) : null}
        <textarea
          className={styles.composer}
          placeholder="Add a note for this visit…"
          ref={composerRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
              event.preventDefault();
              postDraft();
            }
          }}
        />
        <div className={styles.composerButtons}>
          <button
            type="button"
            className={styles.postButton}
            disabled={!draft.trim()}
            onClick={postDraft}
          >
            <StickyNote size={14} aria-hidden="true" />
            Post note
          </button>
        </div>
      </div>
    </>
  );
};

HuddleNotes.propTypes = {
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      author: PropTypes.string,
      createdAt: PropTypes.string,
      editedBy: PropTypes.string,
      editedAt: PropTypes.string,
      source: PropTypes.string,
      visitLabel: PropTypes.string,
    }),
  ),
  onPostNote: PropTypes.func.isRequired,
  onEditNote: PropTypes.func,
  subtitle: PropTypes.string,
  autoFocusComposer: PropTypes.bool,
  composerVisitLabel: PropTypes.string,
  variant: PropTypes.oneOf(['section', 'panel']),
};

export default HuddleNotes;
